import React, { useState, useEffect, useCallback } from 'react';
import './ReviewApplications.css';

function ReviewApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');

  const fetchApplications = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams({
        search: searchQuery,
        status: statusFilter,
        sortBy: sortField,
        sortOrder: sortOrder
      });

      const apiUrl = `https://pardoncms.erichayth.com/api/review?${queryParams.toString()}`;
      const response = await fetch(apiUrl, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.status === 403) {
        setIsAuthenticated(false);
        setError(data.error);
        return;
      }
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch applications');
      }
      
      if (data.success) {
        setIsAuthenticated(true);
        setApplications(data.data || []);
      } else {
        throw new Error(data.error || 'Failed to fetch applications');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, sortField, sortOrder]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleStatusChange = async (appId, newStatus) => {
    try {
      const response = await fetch(`https://pardoncms.erichayth.com/api/application/${appId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update status');
      }

      setApplications(prevApps => 
        prevApps.map(app => 
          app.id === appId ? { ...app, status: newStatus } : app
        )
      );

      if (selectedApp && selectedApp.id === appId) {
        setSelectedApp(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading applications...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="auth-required">
        <h2>Authentication Required</h2>
        <p>
          You need to be authenticated to view applications. Please make sure you're logged in through Cloudflare Access.
        </p>
        <p>
          If you believe you should have access, please contact your administrator.
        </p>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  return (
    <div className="review-container">
      <h1>Review Applications</h1>
      
      {error && (
        <div className="error-banner">{error}</div>
      )}

      <div className="controls-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search applications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-box">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Completed">Completed</option>
          </select>
        </div>

        <div className="sort-box">
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            className="sort-select"
          >
            <option value="created_at">Submission Date</option>
            <option value="updated_at">Last Updated</option>
            <option value="name">Name</option>
            <option value="email">Email</option>
            <option value="status">Status</option>
          </select>
          <button
            onClick={() => setSortOrder(current => current === 'ASC' ? 'DESC' : 'ASC')}
            className="sort-direction-button"
          >
            {sortOrder === 'ASC' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {applications.length === 0 ? (
        <div className="no-data">No applications found</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.id}>
                  <td>{app.name}</td>
                  <td>{app.email}</td>
                  <td className="reason-cell">{app.reason}</td>
                  <td>
                    <span className={`status-badge ${app.status.toLowerCase()}`}>
                      {app.status}
                    </span>
                  </td>
                  <td>{new Date(app.submittedAt).toLocaleString()}</td>
                  <td>{new Date(app.updated_at).toLocaleString()}</td>
                  <td className="actions-cell">
                    <button
                      onClick={() => setSelectedApp(app)}
                      className="view-button"
                    >
                      View
                    </button>
                    <select
                      value={app.status}
                      onChange={(e) => handleStatusChange(app.id, e.target.value)}
                      className="status-select"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedApp && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Application Details</h2>
              <button
                onClick={() => setSelectedApp(null)}
                className="close-button"
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="detail-group">
                <h3>Name</h3>
                <p>{selectedApp.name}</p>
              </div>
              <div className="detail-group">
                <h3>Email</h3>
                <p>{selectedApp.email}</p>
              </div>
              <div className="detail-group">
                <h3>Reason</h3>
                <p className="reason-text">{selectedApp.reason}</p>
              </div>
              <div className="detail-group">
                <h3>Status</h3>
                <p>
                  <span className={`status-badge ${selectedApp.status.toLowerCase()}`}>
                    {selectedApp.status}
                  </span>
                </p>
              </div>
              <div className="detail-group">
                <h3>Submitted</h3>
                <p>{new Date(selectedApp.submittedAt).toLocaleString()}</p>
              </div>
              <div className="detail-group">
                <h3>Last Updated</h3>
                <p>{new Date(selectedApp.updated_at).toLocaleString()}</p>
              </div>
              {selectedApp.fileKey && (
                <div className="detail-group">
                  <h3>Document</h3>
                  <a
                    href={`https://pardoncms.erichayth.com/api/file/${selectedApp.fileKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="document-link"
                  >
                    View Document
                  </a>
                </div>
              )}
              {selectedApp.ocrResult && (
                <div className="detail-group">
                  <h3>OCR Result</h3>
                  <div className="ocr-result">
                    <pre>{selectedApp.ocrResult}</pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReviewApplications; 